import { useEffect, useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useCurrentUserSafe } from '@/contexts/UserContext';

/**
 * useChatNotifications - Hook for chat notification badges and toasts
 * 
 * Now uses UserContext instead of calling getUser() directly.
 */
export const useChatNotifications = () => {
  const [unreadCount, setUnreadCount] = useState(0);
  const userContext = useCurrentUserSafe();
  const userId = userContext?.user?.id;

  const loadUnreadCount = useCallback(async (uid: string) => {
    try {
      const { data, error } = await supabase.rpc('get_total_unread_messages', {
        p_user_id: uid,
      });

      if (error) throw error;

      setUnreadCount(data || 0);
    } catch (error) {
      console.error('Error loading unread count:', error);
      setUnreadCount(0);
    }
  }, []);

  // Load initial count when user is available
  useEffect(() => {
    if (!userId) {
      setUnreadCount(0);
      return;
    }

    loadUnreadCount(userId);
  }, [userId, loadUnreadCount]);

  // Set up realtime subscription
  useEffect(() => {
    if (!userId) return;

    const channel = supabase
      .channel('new-messages-notification')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages',
        },
        async (payload) => {
          const newMessage = payload.new as any;
          
          // Check if this message is for the current user (they are the recipient)
          const { data: conversation } = await supabase
            .from('conversations')
            .select('seller_id, buyer_id, products(title)')
            .eq('id', newMessage.conversation_id)
            .single();

          if (!conversation) return;

          const isRecipient = 
            (conversation.seller_id === userId && newMessage.sender_id !== userId) ||
            (conversation.buyer_id === userId && newMessage.sender_id !== userId);

          if (isRecipient) {
            // Show toast notification
            toast.info('New Message', {
              description: `You have a new message about ${conversation.products?.title || 'a product'}`,
              action: {
                label: 'View',
                onClick: () => window.location.href = '/dashboard/chats',
              },
            });

            // Update unread count
            await loadUnreadCount(userId);

            // Trigger email notification via edge function
            try {
              await supabase.functions.invoke('notify-new-message', {
                body: { messageId: newMessage.id },
              });
            } catch (error) {
              console.error('Error sending email notification:', error);
            }
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'conversation_reads',
        },
        async () => {
          if (userId) {
            await loadUnreadCount(userId);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [userId, loadUnreadCount]);

  return { unreadCount };
};
